import { supabase } from '../lib/supabase';

export interface QRCode {
  id: string;
  code: string;
  hotelName: string | null;
  note: string | null; // NEW: Note field for permanent codes
  status: 'active' | 'redeemed' | 'expired' | 'inactive';
  isActivationCode: boolean;
  isAdminCode: boolean;
  isPermanent: boolean;
  createdAt: string;
  redeemedAt: string | null;
  redeemedBy: string | null;
  redeemedByEmail?: string | null;
  redeemedByName?: string | null;
  expiresAt: string | null;
  createdBy: string | null;
  totalRedemptions?: number;
}

export interface QRCodeRedemption {
  id: string;
  userId: string;
  userEmail: string;
  userName: string;
  redeemedAt: string;
}

export interface QRCodeStats {
  totalActivationCodes: number;
  usedActivationCodes: number;
  availableActivationCodes: number;
  expiredActivationCodes: number;
  totalUsers: number;
  activatedUsers: number;
  unactivatedUsers: number;
  activationRate: number;
  totalPermanentCodes: number; // NEW
  activePermanentCodes: number; // NEW
  inactivePermanentCodes: number; // NEW
  permanentCodeRedemptions: number; // NEW
  uniqueUsersForPermanent: number; // NEW
}

export interface BulkCreateParams {
  hotelName: string;
  count: number;
  expiresInDays?: number;
}

class QRCodeManagementService {
  async getQRCodeStats(): Promise<QRCodeStats> {
    try {
      // Get regular QR code stats
      const { data: regularStats, error: regularError } = await supabase.rpc('get_qr_code_usage_stats');
      
      if (regularError) throw regularError;

      // Get permanent QR code stats
      const { data: permanentStats, error: permanentError } = await supabase.rpc('get_permanent_qr_code_stats');
      
      if (permanentError) throw permanentError;

      const stats = regularStats[0];
      const permStats = permanentStats[0];
      
      const activationRate = stats.total_users > 0 
        ? (stats.activated_users / stats.total_users) * 100 
        : 0;

      return {
        totalActivationCodes: stats.total_activation_codes || 0,
        usedActivationCodes: stats.used_activation_codes || 0,
        availableActivationCodes: stats.available_activation_codes || 0,
        expiredActivationCodes: stats.expired_activation_codes || 0,
        totalUsers: stats.total_users || 0,
        activatedUsers: stats.activated_users || 0,
        unactivatedUsers: stats.unactivated_users || 0,
        activationRate: Math.round(activationRate * 100) / 100,
        totalPermanentCodes: permStats.total_permanent_codes || 0,
        activePermanentCodes: permStats.active_permanent_codes || 0,
        inactivePermanentCodes: permStats.inactive_permanent_codes || 0,
        permanentCodeRedemptions: permStats.total_redemptions || 0,
        uniqueUsersForPermanent: permStats.unique_users || 0
      };
    } catch (error) {
      console.error('Error fetching QR code stats:', error);
      throw error;
    }
  }

  async getAllQRCodes(): Promise<QRCode[]> {
    try {
      const { data: qrCodes, error } = await supabase
        .from('qr_codes')
        .select(`
          id,
          code,
          hotel_name,
          note,
          status,
          is_activation_code,
          is_admin_code,
          is_permanent,
          created_at,
          redeemed_at,
          redeemed_by,
          expires_at,
          created_by
        `)
        .eq('is_activation_code', true)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Fetch user details and redemption counts for each QR code
      const codesWithDetails = await Promise.all(
        (qrCodes || []).map(async (qr) => {
          let redeemedByEmail = null;
          let redeemedByName = null;
          let totalRedemptions = 0;

          // Get user details for redeemed temporary codes
          if (qr.redeemed_by && !qr.is_permanent) {
            const { data: userProfile } = await supabase
              .from('user_profiles')
              .select('email, full_name')
              .eq('id', qr.redeemed_by)
              .single();

            if (userProfile) {
              redeemedByEmail = userProfile.email;
              redeemedByName = userProfile.full_name;
            }
          }

          // Get total redemptions for permanent codes
          if (qr.is_permanent) {
            const { count } = await supabase
              .from('qr_code_redemptions')
              .select('id', { count: 'exact', head: true })
              .eq('qr_code_id', qr.id);
            
            totalRedemptions = count || 0;
          }

          return {
            id: qr.id,
            code: qr.code,
            hotelName: qr.hotel_name,
            note: qr.note,
            status: qr.status,
            isActivationCode: qr.is_activation_code,
            isAdminCode: qr.is_admin_code,
            isPermanent: qr.is_permanent || false,
            createdAt: qr.created_at,
            redeemedAt: qr.redeemed_at,
            redeemedBy: qr.redeemed_by,
            redeemedByEmail,
            redeemedByName,
            expiresAt: qr.expires_at,
            createdBy: qr.created_by,
            totalRedemptions
          };
        })
      );

      return codesWithDetails;
    } catch (error) {
      console.error('Error fetching QR codes:', error);
      throw error;
    }
  }

  async bulkCreateQRCodes(params: BulkCreateParams): Promise<QRCode[]> {
    try {
      const { hotelName, count, expiresInDays } = params;
      const codes: any[] = [];
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Calculate expiration date if provided
      const expiresAt = expiresInDays 
        ? new Date(Date.now() + expiresInDays * 24 * 60 * 60 * 1000).toISOString()
        : null;

      // Generate unique codes
      for (let i = 0; i < count; i++) {
        const sequenceNumber = String(i + 1).padStart(7, '0');
        const code = `${hotelName.toUpperCase()}_${sequenceNumber}`;
        
        codes.push({
          code,
          hotel_name: hotelName,
          is_activation_code: true,
          is_admin_code: false,
          status: 'active',
          created_by: user.id,
          expires_at: expiresAt
        });
      }

      // Insert all codes
      const { data, error } = await supabase
        .from('qr_codes')
        .insert(codes)
        .select();

      if (error) throw error;

      return data.map(qr => ({
        id: qr.id,
        code: qr.code,
        hotelName: qr.hotel_name,
        status: qr.status,
        isActivationCode: qr.is_activation_code,
        isAdminCode: qr.is_admin_code,
        createdAt: qr.created_at,
        redeemedAt: qr.redeemed_at,
        redeemedBy: qr.redeemed_by,
        expiresAt: qr.expires_at,
        createdBy: qr.created_by
      }));
    } catch (error) {
      console.error('Error creating QR codes:', error);
      throw error;
    }
  }

  async bulkDeleteQRCodes(qrCodeIds: string[]): Promise<void> {
    try {
      const { error } = await supabase
        .from('qr_codes')
        .delete()
        .in('id', qrCodeIds);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting QR codes:', error);
      throw error;
    }
  }

  async deleteExpiredQRCodes(): Promise<number> {
    try {
      const now = new Date().toISOString();
      
      const { data, error } = await supabase
        .from('qr_codes')
        .delete()
        .eq('is_activation_code', true)
        .eq('status', 'expired')
        .lt('expires_at', now)
        .select('id');

      if (error) throw error;

      return data?.length || 0;
    } catch (error) {
      console.error('Error deleting expired QR codes:', error);
      throw error;
    }
  }

  async createPermanentQRCode(code: string, note: string): Promise<QRCode> {
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('qr_codes')
        .insert({
          code: code,
          hotel_name: null, // Permanent codes don't have hotel partners
          note: note,
          is_activation_code: true,
          is_admin_code: false,
          is_permanent: true,
          status: 'active',
          created_by: user.id,
          expires_at: null // Permanent codes never expire
        })
        .select()
        .single();

      if (error) throw error;

      return {
        id: data.id,
        code: data.code,
        hotelName: null,
        note: data.note,
        status: data.status,
        isActivationCode: data.is_activation_code,
        isAdminCode: data.is_admin_code,
        isPermanent: data.is_permanent,
        createdAt: data.created_at,
        redeemedAt: null,
        redeemedBy: null,
        expiresAt: null,
        createdBy: data.created_by,
        totalRedemptions: 0
      };
    } catch (error) {
      console.error('Error creating permanent QR code:', error);
      throw error;
    }
  }

  async getQRCodeRedemptions(
    qrCodeId: string, 
    limit: number = 50, 
    offset: number = 0
  ): Promise<{ redemptions: QRCodeRedemption[]; total: number }> {
    try {
      const { data, error } = await supabase.rpc('get_permanent_qr_code_redemptions', {
        p_qr_code_id: qrCodeId,
        p_limit: limit,
        p_offset: offset
      });

      if (error) throw error;

      const redemptions = data.map((r: any) => ({
        id: r.id,
        userId: r.user_id,
        userEmail: r.user_email,
        userName: r.user_name,
        redeemedAt: r.redeemed_at
      }));

      const total = data.length > 0 ? data[0].total_redemptions : 0;

      return { redemptions, total };
    } catch (error) {
      console.error('Error fetching QR code redemptions:', error);
      throw error;
    }
  }

  async deactivateQRCode(qrCodeId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('qr_codes')
        .update({ status: 'inactive' })
        .eq('id', qrCodeId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deactivating QR code:', error);
      throw error;
    }
  }

  async reactivateQRCode(qrCodeId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('qr_codes')
        .update({ status: 'active' })
        .eq('id', qrCodeId);

      if (error) throw error;
    } catch (error) {
      console.error('Error reactivating QR code:', error);
      throw error;
    }
  }

  async deletePermanentQRCode(qrCodeId: string): Promise<void> {
    try {
      // Verify it's a permanent code before deletion
      const { data: qrCode, error: fetchError } = await supabase
        .from('qr_codes')
        .select('is_permanent')
        .eq('id', qrCodeId)
        .single();

      if (fetchError) throw fetchError;
      if (!qrCode.is_permanent) {
        throw new Error('Only permanent QR codes can be deleted through this method');
      }

      const { error } = await supabase
        .from('qr_codes')
        .delete()
        .eq('id', qrCodeId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting permanent QR code:', error);
      throw error;
    }
  }

  async exportQRCodesToCSV(qrCodes: QRCode[]): Promise<void> {
    try {
      const csvContent = [
        ['Code', 'Hotel Name', 'Status', 'Created At', 'Redeemed By', 'Redeemed At', 'Expires At'],
        ...qrCodes.map(qr => [
          qr.code,
          qr.hotelName || 'N/A',
          qr.status,
          new Date(qr.createdAt).toLocaleString(),
          qr.redeemedByName || qr.redeemedByEmail || 'Not Redeemed',
          qr.redeemedAt ? new Date(qr.redeemedAt).toLocaleString() : 'N/A',
          qr.expiresAt ? new Date(qr.expiresAt).toLocaleString() : 'Never'
        ])
      ].map(row => row.join(',')).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `qr-codes-export-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting QR codes:', error);
      throw error;
    }
  }
}

export const qrCodeManagementService = new QRCodeManagementService();